import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Web3Singleton from 'web3Singleton';

import { PurchaseError, PurchaseErrorCode } from 'models/errors/purchase-error';
import { IState } from 'models/reducers/i-state.interface';
import { IWalletCurrencyDetails } from 'models/wallet-currency.interface';

import { CheckoutProgress } from 'enums/checkout-progress.enum';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';

import { addErrorMessageAction } from 'actions/global-actions';
import { postScanTriggerRequestAction } from 'actions/market.action';

import { networkSettingsService } from 'services/network.service';

import { IEvents } from 'hooks/events/useEventsParams';
import { SourceScreenName, useFundsEvents } from 'hooks/events/useFundsEvents';

import { parseDecimal } from 'utils/parser.utils';
import { validateEthereumAddress } from 'utils/validation.utils';

export interface BuyParams {
  nftId?: string;
  nftName?: string;
  fnftsAmount: number;
  amountRmv?: number;
}

/**
 * Shared purchase logic between usePackagePurchase and usePurchase
 * Handles common state management, validation, and blockchain interactions
 */
export const usePurchaseCore = () => {
  const { t } = useTranslation('notifications');
  const dispatch = useDispatch();
  const lgoWeb3 = Web3Singleton.getInstance();
  const accountAddress = useSelector((state: IState): string => state.wallet.address!);

  const [transactionHash, setTransactionHash] = useState('');
  const [confirmationPending, setConfirmationPending] = useState<boolean>(false);
  const [progressStep, setProgressStep] = useState<CheckoutProgress>(CheckoutProgress.start);
  const [fundsRemaining, setFundsRemaining] = useState<number>(0);
  const [fundsRemainingCurrency, setFundsRemainingCurrency] = useState<IWalletCurrencyDetails>();
  const [isAddFundsOpen, setIsAddFundsOpen] = useState<boolean>(false);

  const { sendFundsNotEnoughEvent } = useFundsEvents();

  /**
   * Validates the network matches the expected network
   */
  const validateNetwork = async (expectedNetwork: number): Promise<void> => {
    const network = await lgoWeb3.getChainId();
    if (network !== expectedNetwork) {
      throw PurchaseError.wrongNetwork();
    }
  };

  /**
   * Validates the seller address is valid
   */
  const validateSellerAddress = (sellerAddress: string): void => {
    validateEthereumAddress(sellerAddress, 'Seller address');
  };

  /**
   * Checks if user has sufficient balance for the purchase
   * Returns true if sufficient, false if not (and triggers add funds flow)
   */
  const checkSufficientBalance = (currency: IWalletCurrencyDetails, price: string, buyParams: BuyParams): boolean => {
    const priceNum = parseFloat(parseDecimal(price, currency.decimal));
    const balanceNum = parseFloat(parseDecimal(currency.balance, currency.decimal));
    const remaining = balanceNum - priceNum;

    if (remaining < 0) {
      setFundsRemainingCurrency(currency);
      setFundsRemaining(remaining * -1);
      setIsAddFundsOpen(true);
      sendFundsNotEnoughEvent({
        nftId: buyParams.nftId,
        sourceScreen: SourceScreenName.CompleteCheckoutModal,
      });
      return false;
    }

    return true;
  };

  /**
   * Checks and grants currency permissions if needed
   */
  const ensureCurrencyPermissions = async (currency: IWalletCurrencyDetails, price: string): Promise<void> => {
    const tradeAddress = networkSettingsService().TRADE.ADDRESS;

    const hasCurrencyPermission = await lgoWeb3.checkPermissionToCurrency(
      tradeAddress,
      accountAddress,
      currency,
      +price
    );

    if (!hasCurrencyPermission) {
      try {
        await lgoWeb3.grantCurrencyPermissionServiceWrapper(tradeAddress, currency, accountAddress, price);
      } catch (error) {
        throw PurchaseError.permissionsRejected();
      }
    }
  };

  /**
   * Checks if a signature hash has been used on the blockchain
   */
  const checkSignatureHashUsed = async (signatureHash: string): Promise<boolean> => {
    return await lgoWeb3.isSignatureUsed(signatureHash);
  };

  /**
   * Handles purchase errors and updates UI state accordingly
   */
  const handlePurchaseError = (error: unknown, buyParams: BuyParams, events?: IEvents): void => {
    console.error('Purchase error:', error);

    setConfirmationPending(false);
    setProgressStep(CheckoutProgress.start);

    if (error instanceof PurchaseError) {
      // Map error codes to user-friendly messages
      let errorMessage: string;

      switch (error.code) {
        case PurchaseErrorCode.SIGNATURE_USED:
          errorMessage = t(ERROR_MESSAGES.default);
          break;
        case PurchaseErrorCode.WRONG_NETWORK:
          errorMessage = t(ERROR_MESSAGES.wrongNetwork);
          break;
        case PurchaseErrorCode.PERMISSIONS_REJECTED:
          errorMessage = t(ERROR_MESSAGES.permissionsRejected);
          break;
        case PurchaseErrorCode.USER_CANCELED:
          errorMessage = t(ERROR_MESSAGES.purchaseCanceled);
          break;
        case PurchaseErrorCode.NO_AVAILABLE_OFFERS:
          errorMessage = t(ERROR_MESSAGES.default);
          break;
        default:
          errorMessage = t(ERROR_MESSAGES.purchaseDefault);
      }

      dispatch(addErrorMessageAction(errorMessage));
      events?.sendBuyErrorEvent({ ...buyParams, message: error.message, transactionHash });
    } else {
      // Unknown error type
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch(addErrorMessageAction(t(ERROR_MESSAGES.purchaseDefault)));
      events?.sendBuyErrorEvent({ ...buyParams, message: errorMessage, transactionHash });
    }
  };

  /**
   * Handles successful purchase
   */
  const handlePurchaseSuccess = (buyParams: BuyParams, events?: IEvents): void => {
    dispatch(postScanTriggerRequestAction());
    setProgressStep(CheckoutProgress.end);
    events?.sendBuySuccessEvent({ ...buyParams, transactionHash });
  };

  /**
   * Handles user cancellation of transaction
   */
  const handlePurchaseCanceled = (buyParams: BuyParams, events?: IEvents): void => {
    setConfirmationPending(false);
    setProgressStep(CheckoutProgress.start);
    events?.sendBuyErrorEvent({ ...buyParams, message: 'User denied transaction signature.' });
    dispatch(addErrorMessageAction(t(ERROR_MESSAGES.purchaseCanceled)));
  };

  /**
   * Updates transaction hash and progress when transaction is sent
   */
  const handleTransactionHash = (hash: string, buyParams: BuyParams, events?: IEvents): void => {
    setTransactionHash(hash);
    setProgressStep(CheckoutProgress.inProgress);
    events?.sendBuyEvent({ ...buyParams, transactionHash: hash });
  };

  return {
    // State
    transactionHash,
    confirmationPending,
    progressStep,
    fundsRemaining,
    fundsRemainingCurrency,
    isAddFundsOpen,
    accountAddress,
    lgoWeb3,

    // State setters
    setConfirmationPending,
    setProgressStep,
    setIsAddFundsOpen,

    // Validation helpers
    validateNetwork,
    validateSellerAddress,
    checkSufficientBalance,
    ensureCurrencyPermissions,
    checkSignatureHashUsed,

    // Event handlers
    handlePurchaseError,
    handlePurchaseSuccess,
    handlePurchaseCanceled,
    handleTransactionHash,
  };
};
